
import React, { useState, useEffect, useRef } from 'react';
import { fetchUserTranscripts, deleteFromHistory } from '../services/historyService';
import { getSessionJobs, removeSessionJobsByIds, StorableJob } from '../services/jobService';
import {
  Folder,
  fetchFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  moveTranscriptToFolder,
  pickDefaultColor,
} from '../services/folderService';
import { TranscriptionHistoryItem, TranscriptionStatus, TranscriptionJob } from '../types';
import {
  FileAudio,
  Trash2,
  Inbox,
  Clock,
  ChevronRight,
  Search,
  FolderOpen,
  FolderPlus,
  Folder as FolderIcon,
  MoreVertical,
  Pencil,
  Check,
  X,
  Lock,
} from 'lucide-react';
import JobItem from './JobItem';
import { auth, onAuthStateChanged } from '../services/supabase';
import type { User } from '../services/supabase';

type Tab = 'inprogress' | 'completed' | 'cancelled' | 'failed';

interface HistoryPageProps {
  onNavigateToDashboard: () => void;
  onView: (item: TranscriptionHistoryItem) => void;
  isPaid?: boolean;
}

const formatDuration = (ms: number) => {
  if (ms <= 0) return '';
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
};

const ALL_FOLDER_ID = '__all__';
const UNASSIGNED_FOLDER_ID = '__unassigned__';

const HistoryPage: React.FC<HistoryPageProps> = ({ onNavigateToDashboard, onView, isPaid }) => {
  const [history, setHistory] = useState<TranscriptionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [inProgressJobs, setInProgressJobs] = useState<StorableJob[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('completed');

  // Folder state
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>(ALL_FOLDER_ID);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [folderMenuOpenId, setFolderMenuOpenId] = useState<string | null>(null);
  const [contextMenuItemId, setContextMenuItemId] = useState<string | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const newFolderInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [user, setUser] = useState<User | null>(auth.currentUser);

  const loadHistory = async (currentUser: User | null) => {
    if (!currentUser) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const items = await fetchUserTranscripts(currentUser.id);
      setHistory(items);
    } catch (e) {
      console.error('Failed to fetch history', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadFolders = async (currentUser: User | null) => {
    if (!currentUser || !isPaid) return;
    try {
      const fetched = await fetchFolders();
      setFolders(fetched);
    } catch (e) {
      console.error('Failed to fetch folders', e);
    }
  };

  useEffect(() => {
    setInProgressJobs(getSessionJobs().filter((job) => job.status !== TranscriptionStatus.SUCCESS));
    const currentUser = auth.currentUser;
    loadHistory(currentUser);
    loadFolders(currentUser);

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      loadHistory(currentUser);
      loadFolders(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isCreatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [isCreatingFolder]);

  useEffect(() => {
    if (editingFolderId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingFolderId]);

  // Close menus on outside click
  useEffect(() => {
    const handler = () => {
      setFolderMenuOpenId(null);
      setContextMenuItemId(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleClearCurrentTab = async () => {
    if (!window.confirm('Clear all visible items in this tab? This cannot be undone.')) return;

    if (activeTab === 'completed') {
      await Promise.all(filteredHistory.map((item) => deleteFromHistory(item.id)));
      const removedIds = new Set(filteredHistory.map((item) => item.id));
      setHistory((prev) => prev.filter((item) => !removedIds.has(item.id)));
    } else {
      const jobsToRemove =
        activeTab === 'inprogress' ? filteredActiveJobs
        : activeTab === 'cancelled' ? filteredCancelledJobs
        : filteredFailedJobs;
      removeSessionJobsByIds(jobsToRemove.map((j) => j.id));
      const removedIds = new Set(jobsToRemove.map((j) => j.id));
      setInProgressJobs((prev) => prev.filter((j) => !removedIds.has(j.id)));
    }
  };

  const handleDeleteItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this transcript?')) {
      deleteFromHistory(id);
      setHistory((curr) => curr.filter((item) => item.id !== id));
    }
  };

  const handleDummyView = () => {
    alert('To view a transcript, please re-upload the file from the dashboard.');
    onNavigateToDashboard();
  };

  // --- Folder handlers ---

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const color = pickDefaultColor(folders.length);
      const folder = await createFolder(name, color);
      setFolders((prev) => [...prev, folder]);
      setNewFolderName('');
      setIsCreatingFolder(false);
      setSelectedFolderId(folder.id);
    } catch (e) {
      console.error('Failed to create folder', e);
    }
  };

  const handleRenameFolder = async (folderId: string) => {
    const name = editingName.trim();
    if (!name) {
      setEditingFolderId(null);
      return;
    }
    try {
      await renameFolder(folderId, name);
      setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, name } : f)));
    } catch (e) {
      console.error('Failed to rename folder', e);
    } finally {
      setEditingFolderId(null);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!window.confirm('Delete this folder? Transcripts inside will be moved to the root.')) return;
    try {
      await deleteFolder(folderId);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      setHistory((prev) =>
        prev.map((item) => (item.folderId === folderId ? { ...item, folderId: null } : item)),
      );
      if (selectedFolderId === folderId) setSelectedFolderId(ALL_FOLDER_ID);
    } catch (e) {
      console.error('Failed to delete folder', e);
    }
  };

  const handleMoveToFolder = async (transcriptId: string, folderId: string | null) => {
    try {
      await moveTranscriptToFolder(transcriptId, folderId);
      setHistory((prev) =>
        prev.map((item) => (item.id === transcriptId ? { ...item, folderId } : item)),
      );
    } catch (e) {
      console.error('Failed to move transcript', e);
    } finally {
      setContextMenuItemId(null);
    }
  };

  const handleItemContextMenu = (e: React.MouseEvent, itemId: string) => {
    if (!isPaid) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuItemId(itemId);
  };

  // --- Job buckets ---
  const activeJobs = inProgressJobs.filter(
    (j) => j.status === TranscriptionStatus.UPLOADING || j.status === TranscriptionStatus.PROCESSING,
  );
  const cancelledJobsList = inProgressJobs.filter(
    (j) => j.status === TranscriptionStatus.ERROR && j.error === 'Cancelled',
  );
  const failedJobsList = inProgressJobs.filter(
    (j) => j.status === TranscriptionStatus.ERROR && j.error !== 'Cancelled',
  );

  // --- Filtering ---

  const lowerSearch = searchTerm.toLowerCase();

  const filteredHistory = history.filter((item) => {
    const matchesSearch =
      item.fileName.toLowerCase().includes(lowerSearch) ||
      item.transcript.toLowerCase().includes(lowerSearch);

    if (!matchesSearch) return false;

    if (!isPaid || selectedFolderId === ALL_FOLDER_ID) return true;
    if (selectedFolderId === UNASSIGNED_FOLDER_ID) return !item.folderId;
    return item.folderId === selectedFolderId;
  });

  const filteredActiveJobs = activeJobs.filter((j) =>
    j.fileName.toLowerCase().includes(lowerSearch),
  );
  const filteredCancelledJobs = cancelledJobsList.filter((j) =>
    j.fileName.toLowerCase().includes(lowerSearch),
  );
  const filteredFailedJobs = failedJobsList.filter((j) =>
    j.fileName.toLowerCase().includes(lowerSearch),
  );

  const folderItemCount = (folderId: string) => {
    if (folderId === ALL_FOLDER_ID) return history.length;
    if (folderId === UNASSIGNED_FOLDER_ID) return history.filter((i) => !i.folderId).length;
    return history.filter((i) => i.folderId === folderId).length;
  };

  const selectedFolder = folders.find((f) => f.id === selectedFolderId);

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'inprogress', label: 'In Progress', count: activeJobs.length },
    { id: 'completed', label: 'Completed', count: history.length },
    { id: 'cancelled', label: 'Cancelled', count: cancelledJobsList.length },
    { id: 'failed', label: 'Failed', count: failedJobsList.length },
  ];

  const currentTabFilteredCount =
    activeTab === 'inprogress' ? filteredActiveJobs.length
    : activeTab === 'completed' ? filteredHistory.length
    : activeTab === 'cancelled' ? filteredCancelledJobs.length
    : filteredFailedJobs.length;

  const renderEmptyJobState = (message: string) => (
    <div className="flex flex-col items-center justify-center py-20 bg-base-200/50 dark:bg-dark-200/50 border-2 border-dashed border-base-300 dark:border-dark-300 rounded-2xl">
      <div className="w-16 h-16 bg-base-200 dark:bg-dark-300 rounded-full flex items-center justify-center mb-4">
        <Inbox className="w-8 h-8 text-base-content/40 dark:text-dark-content/40" />
      </div>
      <p className="text-base-content/60 dark:text-dark-content/60">{message}</p>
    </div>
  );

  const pageTitle =
    activeTab === 'completed'
      ? selectedFolderId === ALL_FOLDER_ID
        ? 'History'
        : selectedFolderId === UNASSIGNED_FOLDER_ID
        ? 'Unassigned'
        : (selectedFolder?.name ?? 'History')
      : tabs.find((t) => t.id === activeTab)?.label ?? 'History';

  return (
    <div className="w-full max-w-6xl animate-fade-in px-4 pb-12">
      {/* Page header — always visible */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h2 className="text-3xl font-bold text-base-content dark:text-dark-content tracking-tight">
            {pageTitle}
          </h2>
          <p className="text-base-content/60 dark:text-dark-content/60">Manage your previous transcriptions</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-grow md:flex-grow-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" size={18} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-base-200 dark:bg-dark-200 border border-base-300 dark:border-dark-300 rounded-xl w-full md:w-64 focus:ring-2 focus:ring-brand-primary focus:outline-none transition-all"
            />
          </div>
          {currentTabFilteredCount > 0 && (
            <button
              onClick={handleClearCurrentTab}
              className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 hover:bg-red-500/20 px-4 py-2 rounded-xl transition-colors whitespace-nowrap"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Clear All</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-base-200 dark:border-dark-300">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-base-content/50 dark:text-dark-content/50 hover:text-base-content dark:hover:text-dark-content'
            }`}
          >
            {tab.label}
            <span
              className={`text-xs font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                activeTab === tab.id
                  ? 'bg-brand-primary/10 text-brand-primary'
                  : 'bg-base-200 dark:bg-dark-300 text-base-content/50 dark:text-dark-content/50'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* In Progress Tab */}
      {activeTab === 'inprogress' && (
        <div className="space-y-3">
          {filteredActiveJobs.length === 0
            ? renderEmptyJobState(searchTerm ? `No results for "${searchTerm}"` : 'No jobs currently in progress.')
            : filteredActiveJobs.map((job) => (
                <JobItem key={job.id} job={job as TranscriptionJob} onView={handleDummyView} />
              ))}
        </div>
      )}

      {/* Cancelled Tab */}
      {activeTab === 'cancelled' && (
        <div className="space-y-3">
          {filteredCancelledJobs.length === 0
            ? renderEmptyJobState(searchTerm ? `No results for "${searchTerm}"` : 'No cancelled jobs.')
            : filteredCancelledJobs.map((job) => (
                <JobItem key={job.id} job={job as TranscriptionJob} onView={handleDummyView} />
              ))}
        </div>
      )}

      {/* Failed Tab */}
      {activeTab === 'failed' && (
        <div className="space-y-3">
          {filteredFailedJobs.length === 0
            ? renderEmptyJobState(searchTerm ? `No results for "${searchTerm}"` : 'No failed jobs.')
            : filteredFailedJobs.map((job) => (
                <JobItem key={job.id} job={job as TranscriptionJob} onView={handleDummyView} />
              ))}
        </div>
      )}

      {/* Completed Tab */}
      {activeTab === 'completed' && (
        <div className="flex gap-6">
          {/* Folder Sidebar */}
          {isPaid && (
            <aside className="w-56 flex-shrink-0">
              <div className="bg-white dark:bg-dark-200 border border-base-200 dark:border-dark-300 rounded-2xl p-3 space-y-1 sticky top-6">
                <p className="text-xs font-semibold text-base-content/50 dark:text-dark-content/50 uppercase tracking-wider px-2 pb-1">Folders</p>

                {/* All */}
                <FolderNavItem
                  label="All Transcripts"
                  count={folderItemCount(ALL_FOLDER_ID)}
                  selected={selectedFolderId === ALL_FOLDER_ID}
                  onClick={() => setSelectedFolderId(ALL_FOLDER_ID)}
                  icon={<FolderOpen size={15} />}
                />

                {/* User folders */}
                {folders.map((folder) => (
                  <div key={folder.id} className="relative group/folder-item">
                    {editingFolderId === folder.id ? (
                      <div className="flex items-center gap-1 px-2 py-1">
                        <input
                          ref={editInputRef}
                          className="flex-1 text-sm bg-base-100 dark:bg-dark-300 border border-brand-primary rounded px-1 py-0.5 focus:outline-none"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameFolder(folder.id);
                            if (e.key === 'Escape') setEditingFolderId(null);
                          }}
                        />
                        <button onClick={() => handleRenameFolder(folder.id)} className="text-brand-primary"><Check size={13} /></button>
                        <button onClick={() => setEditingFolderId(null)} className="text-base-content/40"><X size={13} /></button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedFolderId(folder.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                          selectedFolderId === folder.id
                            ? 'bg-brand-primary/10 text-brand-primary font-semibold'
                            : 'text-base-content/70 dark:text-dark-content/70 hover:bg-base-100 dark:hover:bg-dark-300'
                        }`}
                      >
                        <span style={{ color: folder.color }}><FolderIcon size={15} /></span>
                        <span className="flex-1 text-left truncate">{folder.name}</span>
                        <span className="text-xs text-base-content/40 dark:text-dark-content/40 tabular-nums">
                          {folderItemCount(folder.id)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFolderMenuOpenId(folderMenuOpenId === folder.id ? null : folder.id);
                          }}
                          className="opacity-0 group-hover/folder-item:opacity-100 p-0.5 rounded hover:bg-base-200 dark:hover:bg-dark-400 transition-opacity"
                        >
                          <MoreVertical size={12} />
                        </button>
                      </button>
                    )}

                    {/* Folder context menu */}
                    {folderMenuOpenId === folder.id && (
                      <div
                        className="absolute right-0 top-8 z-50 bg-white dark:bg-dark-200 border border-base-200 dark:border-dark-300 rounded-xl shadow-lg py-1 min-w-[120px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-base-100 dark:hover:bg-dark-300 flex items-center gap-2"
                          onClick={() => {
                            setEditingFolderId(folder.id);
                            setEditingName(folder.name);
                            setFolderMenuOpenId(null);
                          }}
                        >
                          <Pencil size={13} /> Rename
                        </button>
                        <button
                          className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          onClick={() => {
                            setFolderMenuOpenId(null);
                            handleDeleteFolder(folder.id);
                          }}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Unassigned */}
                <FolderNavItem
                  label="Unassigned"
                  count={folderItemCount(UNASSIGNED_FOLDER_ID)}
                  selected={selectedFolderId === UNASSIGNED_FOLDER_ID}
                  onClick={() => setSelectedFolderId(UNASSIGNED_FOLDER_ID)}
                  icon={<FolderIcon size={15} className="opacity-40" />}
                />

                <div className="pt-1 border-t border-base-200 dark:border-dark-300">
                  {isCreatingFolder ? (
                    <div className="flex items-center gap-1 px-2 py-1">
                      <input
                        ref={newFolderInputRef}
                        className="flex-1 text-sm bg-base-100 dark:bg-dark-300 border border-brand-primary rounded px-1 py-0.5 focus:outline-none"
                        placeholder="Folder name..."
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateFolder();
                          if (e.key === 'Escape') { setIsCreatingFolder(false); setNewFolderName(''); }
                        }}
                      />
                      <button onClick={handleCreateFolder} className="text-brand-primary"><Check size={13} /></button>
                      <button onClick={() => { setIsCreatingFolder(false); setNewFolderName(''); }} className="text-base-content/40"><X size={13} /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsCreatingFolder(true)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-base-content/50 dark:text-dark-content/50 hover:bg-base-100 dark:hover:bg-dark-300 transition-colors"
                    >
                      <FolderPlus size={15} />
                      New folder
                    </button>
                  )}
                </div>
              </div>
            </aside>
          )}

          {/* Upsell sidebar for free users */}
          {!isPaid && user && (
            <aside className="w-52 flex-shrink-0">
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-brand-primary">
                  <Lock size={16} />
                  <span className="text-sm font-semibold">Folders</span>
                </div>
                <p className="text-xs text-base-content/60 dark:text-dark-content/60">
                  Organize your transcripts into folders. Available on the paid plan.
                </p>
                <a
                  href="/pricing"
                  className="block text-center text-xs font-semibold text-white bg-brand-primary hover:bg-brand-dark px-3 py-1.5 rounded-lg transition-colors"
                >
                  Upgrade
                </a>
              </div>
            </aside>
          )}

          {/* Transcript list */}
          <section className="flex-1 min-w-0">
            {!user ? (
              <div className="flex flex-col items-center justify-center py-20 bg-base-200/50 dark:bg-dark-200/50 border-2 border-dashed border-base-300 dark:border-dark-300 rounded-2xl">
                <div className="w-16 h-16 bg-base-200 dark:bg-dark-300 rounded-full flex items-center justify-center mb-4">
                  <Inbox className="w-8 h-8 text-base-content/40 dark:text-dark-content/40" />
                </div>
                <h3 className="text-xl font-bold text-base-content dark:text-dark-content">Sign in to see history</h3>
                <p className="text-base-content/60 dark:text-dark-content/60 mt-1 max-w-xs text-center">
                  Your transcripts are saved to your account and available across devices.
                </p>
              </div>
            ) : historyLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-base-200/50 dark:bg-dark-200/50 border-2 border-dashed border-base-300 dark:border-dark-300 rounded-2xl">
                <div className="w-16 h-16 bg-base-200 dark:bg-dark-300 rounded-full flex items-center justify-center mb-4">
                  <Inbox className="w-8 h-8 text-base-content/40 dark:text-dark-content/40" />
                </div>
                <h3 className="text-xl font-bold text-base-content dark:text-dark-content">No History Yet</h3>
                <p className="text-base-content/60 dark:text-dark-content/60 mt-1 max-w-xs text-center mb-6">
                  Your transcribed files will appear here once you process them.
                </p>
                <button
                  onClick={onNavigateToDashboard}
                  className="bg-brand-primary text-white font-medium px-6 py-2 rounded-xl hover:bg-brand-dark transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-base-content/60">
                  {searchTerm ? `No results found for "${searchTerm}"` : 'No transcripts in this folder'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredHistory.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onView(item)}
                    onContextMenu={(e) => handleItemContextMenu(e, item.id)}
                    className="group bg-white dark:bg-dark-200 p-5 rounded-2xl border border-base-200 dark:border-dark-300 hover:border-brand-primary/40 dark:hover:border-brand-primary/40 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-5 overflow-hidden">
                      <div className="w-12 h-12 bg-brand-primary/5 rounded-xl flex items-center justify-center text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors duration-300">
                        <FileAudio className="w-6 h-6" />
                      </div>
                      <div className="overflow-hidden space-y-1">
                        <p className="font-bold text-lg text-base-content dark:text-dark-content truncate" title={item.fileName}>
                          {item.fileName}
                        </p>
                        <div className="flex items-center gap-x-4 text-xs text-base-content/60 dark:text-dark-content/60 font-medium">
                          <span>
                            {new Date(item.timestamp).toLocaleDateString()} at{' '}
                            {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {item.duration > 0 && (
                            <span className="flex items-center gap-1 bg-base-200 dark:bg-dark-300 px-2 py-0.5 rounded-full">
                              <Clock size={10} />
                              {formatDuration(item.duration)}
                            </span>
                          )}
                          {isPaid && item.folderId && folders.length > 0 && (
                            <span
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: `${folders.find((f) => f.id === item.folderId)?.color ?? '#6366f1'}20`,
                                color: folders.find((f) => f.id === item.folderId)?.color ?? '#6366f1',
                              }}
                            >
                              <FolderIcon size={10} />
                              {folders.find((f) => f.id === item.folderId)?.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="hidden sm:flex text-sm font-semibold text-brand-primary opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0 duration-300">
                        View
                      </span>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-base-200 dark:hover:bg-dark-300 transition-colors">
                        <ChevronRight className="w-5 h-5 text-base-content/30 group-hover:text-brand-primary transition-colors" />
                      </div>
                      <div className="h-8 w-px bg-base-300 dark:bg-dark-300 mx-1" />
                      <button
                        onClick={(e) => handleDeleteItem(item.id, e)}
                        className="p-2 text-base-content/40 dark:text-dark-content/40 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Context menu for moving transcripts to folders */}
      {contextMenuItemId && isPaid && (
        <div
          className="fixed z-50 bg-white dark:bg-dark-200 border border-base-200 dark:border-dark-300 rounded-xl shadow-xl py-1 min-w-[180px]"
          style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-3 py-1 text-xs font-semibold text-base-content/40 uppercase tracking-wider">Move to folder</p>
          <button
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-base-100 dark:hover:bg-dark-300 flex items-center gap-2"
            onClick={() => handleMoveToFolder(contextMenuItemId, null)}
          >
            <FolderIcon size={14} className="opacity-40" /> Unassigned
          </button>
          {folders.map((folder) => (
            <button
              key={folder.id}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-base-100 dark:hover:bg-dark-300 flex items-center gap-2"
              onClick={() => handleMoveToFolder(contextMenuItemId, folder.id)}
            >
              <FolderIcon size={14} style={{ color: folder.color }} />
              {folder.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface FolderNavItemProps {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

const FolderNavItem: React.FC<FolderNavItemProps> = ({ label, count, selected, onClick, icon }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
      selected
        ? 'bg-brand-primary/10 text-brand-primary font-semibold'
        : 'text-base-content/70 dark:text-dark-content/70 hover:bg-base-100 dark:hover:bg-dark-300'
    }`}
  >
    <span>{icon}</span>
    <span className="flex-1 text-left truncate">{label}</span>
    <span className="text-xs text-base-content/40 dark:text-dark-content/40 tabular-nums">{count}</span>
  </button>
);

export default HistoryPage;
